// deploy-pipelines-run-provenance (Wave 2 of deploy-pipelines-detail-redesign) — resolves a run's
// "triggered by" fact into exactly one of THREE defined states. Extracted as a pure helper (no React,
// no network) specifically so AC-3/AC-4/AC-4a/AC-15 get a real pure-logic-jest tier — the case file's
// D4 note assumed this slice would extract none.
//
// The three states have three DIFFERENT causes and must stay distinguishable in the data even where
// two of them render the same word:
//   • 'system'  — CreatedBy is Guid.Empty, the ALREADY-MODELLED system actor (AC-3, spec F1c —
//                 AuditRecorder.cs:51 `actAsSystem || _user.UserId == Guid.Empty`). Not a capture
//                 failure: no human identity ever existed for this run.
//   • 'unknown' — no usable captured name, for either of two reasons that are indistinguishable at
//                 render time but distinct in the data (both carry a real, non-empty CreatedBy):
//                   AC-4  the run predates this slice, so the column did not exist when it was written
//                         and NO lookup exists to backfill it (spec F1b) — permanent by design;
//                   AC-4a a live post-slice trigger whose claims carried no usable display name.
//   • 'named'   — a captured display name, rendered verbatim.
//
// AC-15, and the whole point of isolating this: NO name is ever invented, guessed, derived or
// reformatted — most importantly never from the email's local-part. Unknown means Unknown. `email` is
// passed through verbatim (or null) and NEVER gates rendering the name (TC-DPRP-009).
// AC-14: this helper never returns CreatedBy in any field, so no caller can render the raw GUID.

// The all-zero GUID as the API serialises it. Compared case-insensitively because GUID casing is not
// contractually pinned anywhere on the wire.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === '';

export const resolveRunActor = (run) => {
  if (!run) {
    return { kind: 'unknown', name: null, email: null };
  }

  const email = isBlank(run.TriggeredByEmail) ? null : run.TriggeredByEmail;

  // AC-3 — checked FIRST and independently of whether a name was captured: the system actor is a
  // distinct cause, not a flavour of "no name", and must never collapse into 'unknown'.
  if (!isBlank(run.CreatedBy) && String(run.CreatedBy).toLowerCase() === EMPTY_GUID) {
    return { kind: 'system', name: null, email };
  }

  // AC-4 / AC-4a — a real actor id, but no usable captured name. Deliberately NOT falling back to the
  // email, an initial, a placeholder, or anything else derivable (AC-15).
  if (isBlank(run.TriggeredByUserName)) {
    return { kind: 'unknown', name: null, email };
  }

  return { kind: 'named', name: run.TriggeredByUserName, email };
};

export default resolveRunActor;
